import React from 'react'
import TimeIcon from '@mui/icons-material/AccessTime'
import CalendarIcon from '@mui/icons-material/CalendarMonth'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton/IconButton'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CalendarViewDay from '@mui/icons-material/CalendarViewDay'
import CalendarViewWeek from '@mui/icons-material/CalendarViewWeek'
import CalendarViewMonth from '@mui/icons-material/CalendarViewMonth'
import format from 'date-fns/format'
import {
  subMonths,
  subYears,
  subDays,
  subWeeks,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  startOfYear,
  endOfYear,
} from 'date-fns'
import axios from 'axios'
import { useAuth } from '../../context/useAuth'
import { useParams } from 'react-router-dom'

interface Props {
  setData: any
  setError: any
  scenario?: any
}

export default function ResolutionPicker({ setData, setError, scenario }: Props) {
  const { name } = useParams()
  const { simulation } = useAuth()

  const [resolution, setResolution] = React.useState('week_time')
  const [currentDate, setCurrentDate] = React.useState(new Date(simulation.start_time))
  const [start, setStart] = React.useState(startOfMonth(new Date(simulation.start_time)))
  const [end, setEnd] = React.useState(endOfMonth(new Date(simulation.end_time)))

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setError(undefined)
        const { data: ld } = await axios.get(
          `${process.env.REACT_APP_API}/${name}/results/line/${resolution}/${format(start, 'yyyyMMddHHmmss')}/${format(
            end,
            'yyyyMMddHHmmss'
          )}${scenario ? '/' + scenario : ''}`,
          {
            withCredentials: true,
          }
        )

        const { data: kpis } = await axios.get(
          `${process.env.REACT_APP_API}/${name}/results/kpis/${resolution}/${format(start, 'yyyyMMddHHmmss')}/${format(
            end,
            'yyyyMMddHHmmss'
          )}${scenario ? '/' + scenario : ''}`,
          {
            withCredentials: true,
          }
        )
        setData({ lineData: ld, kpis })
      } catch (err: any) {
        const error = err
        if (error.response.data._error) setError(error.response.data._error)
      }
    }
    fetchData()
  }, [resolution, currentDate, end, name, scenario, setData, setError, start])

  const getViewTitle = () => {
    switch (resolution) {
      case 'day':
        return format(currentDate, 'EE dd/MM/yyyy')
      case 'week_time':
        return (
          format(startOfWeek(currentDate, { weekStartsOn: 1 }), `'Week' I':' dd/MM/yyyy`) +
          ' - ' +
          format(endOfWeek(currentDate, { weekStartsOn: 1 }), `dd/MM/yyyy`)
        )
      case 'week_calendar':
        return (
          format(startOfWeek(currentDate, { weekStartsOn: 1 }), `'Week' I':' dd/MM/yyyy`) +
          ' - ' +
          format(endOfWeek(currentDate, { weekStartsOn: 1 }), `dd/MM/yyyy`)
        )
      case 'month':
        return format(currentDate, 'LLLL yyyy')
      case 'year':
        return format(currentDate, 'yyyy')
    }
  }

  const onIntervalClick = (resolution: string) => {
    switch (resolution) {
      case 'day':
        setStart(startOfDay(currentDate))
        setEnd(endOfDay(currentDate))
        setResolution('day')
        break
      case 'week_time':
        setStart(startOfWeek(currentDate, { weekStartsOn: 1 }))
        setEnd(endOfWeek(currentDate, { weekStartsOn: 1 }))
        setResolution('week_time')
        break
      case 'week_calendar':
        setStart(startOfWeek(currentDate, { weekStartsOn: 1 }))
        setEnd(endOfWeek(currentDate, { weekStartsOn: 1 }))
        setResolution('week_calendar')
        break
      case 'month':
        setStart(startOfMonth(currentDate))
        setEnd(endOfMonth(currentDate))
        setResolution('month')
        break
      case 'year':
        setStart(startOfYear(currentDate))
        setEnd(endOfYear(currentDate))
        setResolution('year')
        break
    }
  }

  const onArrowClick = (direction: string) => {
    let newDate
    switch (resolution) {
      case 'day':
        newDate = subDays(currentDate, direction === 'right' ? -1 : 1)
        setStart(startOfDay(newDate))
        setEnd(endOfDay(newDate))
        setCurrentDate(newDate)
        break
      case 'week_time':
        newDate = subWeeks(currentDate, direction === 'right' ? -1 : 1)
        setStart(startOfWeek(newDate, { weekStartsOn: 1 }))
        setEnd(endOfWeek(newDate, { weekStartsOn: 1 }))
        setCurrentDate(newDate)
        break
      case 'week_calendar':
        newDate = subWeeks(currentDate, direction === 'right' ? -1 : 1)
        setStart(startOfWeek(newDate, { weekStartsOn: 1 }))
        setEnd(endOfWeek(newDate, { weekStartsOn: 1 }))
        setCurrentDate(newDate)
        break
      case 'month':
        newDate = subMonths(currentDate, direction === 'right' ? -1 : 1)
        setStart(startOfMonth(newDate))
        setEnd(endOfMonth(newDate))
        setCurrentDate(newDate)
        break
      case 'year':
        newDate = subYears(currentDate, direction === 'right' ? -1 : 1)
        setStart(startOfYear(newDate))
        setEnd(endOfYear(newDate))
        setCurrentDate(newDate)
        break
    }
  }

  return (
    <Grid container alignItems='center' sx={{ bgcolor: 'text.primary', py: [0.75], pl: 1, borderRadius: 1 }}>
      <IconButton
        sx={{ color: 'white' }}
        onClick={() => onArrowClick('left')}
        disabled={start <= new Date(simulation.start_time)}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Typography color={'white'}>{getViewTitle()}</Typography>
      <IconButton
        sx={{ color: 'white', mr: 1 }}
        onClick={() => onArrowClick('right')}
        disabled={end >= new Date(simulation.end_time)}
      >
        <ArrowForwardIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Button
        disableElevation
        startIcon={<CalendarViewDay />}
        sx={{ color: 'white', px: 1 }}
        variant={resolution === 'day' ? 'contained' : 'text'}
        onClick={() => onIntervalClick('day')}
      >
        Day
      </Button>
      <Button
        disableElevation
        startIcon={<TimeIcon />}
        sx={{ color: 'white', ml: 1, px: 1 }}
        variant={resolution === 'week_time' ? 'contained' : 'text'}
        onClick={() => onIntervalClick('week_time')}
      >
        Week
      </Button>
      <Button
        disableElevation
        startIcon={<CalendarViewWeek />}
        sx={{ color: 'white', ml: 1, px: 1 }}
        variant={resolution === 'week_calendar' ? 'contained' : 'text'}
        onClick={() => onIntervalClick('week_calendar')}
      >
        Week
      </Button>
      <Button
        disableElevation
        startIcon={<CalendarViewMonth />}
        sx={{ color: 'white', ml: 1, px: 1 }}
        variant={resolution === 'month' ? 'contained' : 'text'}
        onClick={() => onIntervalClick('month')}
      >
        Month
      </Button>
      <Button
        disableElevation
        startIcon={<CalendarIcon />}
        sx={{ color: 'white', ml: 1, px: 1 }}
        variant={resolution === 'year' ? 'contained' : 'text'}
        onClick={() => onIntervalClick('year')}
      >
        Year
      </Button>
    </Grid>
  )
}
